import { Avatar, Popper, styled } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Checkbox } from '../../../../components/inputs/Checkbox'
import { Text } from '../../../../components/typography'
import { ProjectMemberSearchOption } from '../../../../../domain/value-object/ProjectMemberSearchOption'
import { colorPalette } from '../../../../style/colorPalette'

const StyledPopper = styled(Popper)({
  zIndex: 1400,
  backgroundColor: colorPalette.monotone[0],
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  maxHeight: '300px',
  overflowY: 'auto',
  padding: '16px',
  borderRadius: '4px',
})

const MemberListArea = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
})

const OptionRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const MemberAvatar = styled(Avatar)({
  width: '20px',
  height: '20px',
  marginRight: '5px',
})

const OptionLabel = styled(Text)({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: 1,
})

const AccordionHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '8px 0',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const ArrowIcon = styled(ChevronRightIcon, {
  shouldForwardProp: prop => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
  fontSize: '16px',
  transition: 'transform 0.2s',
  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
}))

type ProjectMemberCheckBoxPopperProps = {
  open: boolean
  anchorEl: Element | undefined
  options: ProjectMemberSearchOption[]
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
}

export const ProjectMemberCheckBoxPopper = ({
  open,
  anchorEl,
  options,
  value,
  onChange,
}: ProjectMemberCheckBoxPopperProps) => {
  const intl = useIntl()
  const [displayReleased, setDisplayReleased] = useState<boolean>(false)
  const paperRef = useRef<HTMLDivElement>(null)

  const members = useMemo(
    () => options.filter(option => !option.released),
    [options]
  )

  const released = useMemo(
    () => options.filter(option => option.released),
    [options]
  )

  const toggleMember = useCallback(
    (uuid: string) => {
      const current = value || []
      if (current.includes(uuid)) {
        const newValue = current.filter(v => v !== uuid)
        onChange(newValue.length > 0 ? newValue : undefined)
      } else {
        onChange([...current, uuid])
      }
    },
    [value, onChange]
  )

  useLayoutEffect(() => {
    if (displayReleased && paperRef.current && released.length > 0) {
      const paper = paperRef.current
      if (released.length <= 5) {
        paper.scrollTop = paper.scrollHeight
      } else {
        const releasedElements = paper.querySelectorAll(
          '[data-released="true"]'
        )
        if (releasedElements.length >= 2) {
          releasedElements[2].scrollIntoView({ block: 'end' })
        }
      }
    }
  }, [displayReleased, released.length])

  const renderMemberRow = (
    option: ProjectMemberSearchOption,
    isReleased?: boolean
  ) => (
    <OptionRow
      key={option.uuid}
      data-released={isReleased || undefined}
      onClick={() => toggleMember(option.uuid)}
    >
      <Checkbox
        sx={{ marginRight: '4px' }}
        checked={(value || []).includes(option.uuid)}
        size="s"
      />
      <MemberAvatar src={option.iconUrl ?? undefined} />
      <OptionLabel color={colorPalette.monotone[4]}>{option.name}</OptionLabel>
    </OptionRow>
  )

  return (
    <StyledPopper
      ref={paperRef}
      open={open && anchorEl != null}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[
        {
          name: 'offset',
          options: { offset: [0, 20] },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'document',
            padding: 8,
          },
        },
      ]}
    >
      {members.length > 0 && (
        <MemberListArea>
          {members.map(option => renderMemberRow(option))}
        </MemberListArea>
      )}
      {released.length > 0 && (
        <AccordionHeader onClick={() => setDisplayReleased(prev => !prev)}>
          <ArrowIcon expanded={displayReleased} />
          <Text variant="s">
            {intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.projectMember.released',
            })}
          </Text>
        </AccordionHeader>
      )}
      {displayReleased &&
        released.length > 0 &&
        released.map(option => renderMemberRow(option, true))}
    </StyledPopper>
  )
}
